import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useClerk } from '@clerk/clerk-react';
import { usePostHog } from 'posthog-js/react';

/**
 * Clerk-based auth modal handler
 * Integrates Clerk's modal system with the existing AuthModalContext logic
 */
export default function ClerkAuthModal() {
  const { isOpen, closeAuthModal, config } = useAuthModal();
  const { isAuthenticated } = useAuth();
  const { openSignIn } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();

  // Open Clerk modal when isOpen is true
  useEffect(() => {
    if (isOpen && !isAuthenticated) {
      // Open Clerk's sign-in modal
      openSignIn({
        afterSignInUrl: config?.afterAuthRedirect || '/app',
        afterSignUpUrl: config?.afterAuthRedirect || '/app',
      });
    }
  }, [isOpen, isAuthenticated, openSignIn, config?.afterAuthRedirect]);

  // Close modal and handle post-auth actions when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      const forceAuth = config?.forceAuth;
      const postAuthAction = config?.postAuthAction;
      
      // If forceAuth is true, execute postAuthAction immediately if user is already authenticated
      if (forceAuth && postAuthAction) {
        try {
          postAuthAction();
          closeAuthModal();
          return;
        } catch (error) {
          console.error('Error executing postAuthAction:', error);
        }
      }
      
      // Normal flow: small delay to allow auth state to fully update
      const timer = setTimeout(() => {
        const redirectTo = config?.afterAuthRedirect;
        
        // Execute postAuthAction if provided
        if (postAuthAction) {
          try {
            postAuthAction();
          } catch (error) {
            console.error('Error executing postAuthAction:', error);
          }
        }
        
        // Navigate to redirect target if no postAuthAction handled it
        if (redirectTo && !postAuthAction) {
          navigate(redirectTo, { replace: true });
        }
        
        closeAuthModal();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isOpen, closeAuthModal, config, navigate]);

  // Track unexpected modal opens for debugging
  useEffect(() => {
    if (isOpen) {
      const currentPath = location.pathname;
      
      if (currentPath === '/upgrade' && isAuthenticated) {
        console.warn('[ClerkAuthModal] ⚠️ Auth modal opened on /upgrade for authenticated user');
        
        posthog?.capture('unexpected_auth_modal_open', {
          page: currentPath,
          is_authenticated: isAuthenticated,
          config_reason: config?.reason,
          config_from_path: config?.fromPath,
          config_redirect: config?.afterAuthRedirect,
        });
      }
    }
  }, [isOpen, isAuthenticated, location.pathname, config, posthog]);

  // Don't render anything - Clerk handles the UI
  return null;
}
